import axios from "vue-app/utils/axios";
import { SMSCredit, Sender } from "vue-app/components/SMS/SMS.types.ts";
import {
  DTOSmsSubscriptionResponse,
  DTOSmsPredictedAudienceResponse,
  DTOEngagementMessageSettings,
  DTOEngagementsSmsSuggestion,
  DTOEngagementSmsSuggestion,
  DTOCreateSubscriptionResponse,
  DTOPriceTiersResponse,
  DTOEngagementMessage,
} from "vue-app/api/dto.d";
import { NewsletterConfig } from "vue-app/pages/PageEmailNewsletter/PageEmailNewsletter.types.ts";
import { useCaptcha } from "vue-app/composables/useCaptcha.ts";

const getSMSPackages = async () => {
  const { data } = await axios.get<DTOPriceTiersResponse>("/sms/packages");
  return data;
};

const getSMSCredits = async () => {
  const { data } = await axios.get<SMSCredit[]>("/sms/credits");
  return data;
};

const getSMSStatistics = async () => {
  const { data } = await axios.get<DTOSmsPredictedAudienceResponse>("/emaillabs/sms/predicted-audience");
  return data;
};

const getSMSSubscription = async () => {
  const { data } = await axios.get<DTOSmsSubscriptionResponse>("/sms/subscription");
  return data;
};

const deleteSMSSubscription = async () => {
  const { data } = await axios.delete("/sms/subscription");
  return data;
};

const postSMSSubscription = async (packageSize: number, promotionCode?: string | null) => {
  let query = `/sms/subscription?package_size=${packageSize}`;
  if (promotionCode) {
    query += `&promotion_code=${promotionCode}`;
  }
  const { data } = await axios.post<DTOCreateSubscriptionResponse>(query);
  return data;
};

const getNewsletterHubSMSCampaignPreview = async (engagementId: number) => {
  const { data } = await axios.get(`/newsletter-hub/${engagementId}/sms-campaign/preview`);
  return data;
};

const postNewsletterHubSMSCampaignCreate = async (engagementId: number, smsContent: string, shortenLinks: boolean) => {
  const { data } = await axios.post(`/newsletter-hub/${engagementId}/sms-campaign/create`, {
    sms_content: smsContent,
    shorten_links: shortenLinks,
  });
  return data;
};

const postNewsletterHubCreateNewSMSCampaign = async (content?: string) => {
  const { data } = await axios.post("/json/newsletter-hub/create-new-sms-campaign", {
    content,
  });
  return data.data;
};

const postNewsletterHubRun = async (engagementId: number) => {
  const response = await axios.post("/newsletter-hub/run", {
    engagement_id: engagementId.toString(),
    tracker_id: window.EDRONE_CONTEXT?.trackerId,
    agent_id: window.EDRONE_CONTEXT?.user?.agentId?.toString(),
  });
  if (!response.data.data.runSuccessful) {
    window.showToast(window._trans._common.engagementRunnability[response.data.data.engagementRunnability], "error");
    if (response.data.data.details && response.data.data.details.includes("File type not supported")) {
      window.showToast(window._trans._whatsApp.errors.fileTypeNotSupported, "error");
    }
    throw new Error(response.data.data.engagementRunnability);
  }
};

const toggleShortenLinks = async (engagementMessageId: number, shortenLinks: boolean) => {
  await axios.post("/engagements/engagement-message/" + engagementMessageId + "/shorten-links", {
    shorten_links: shortenLinks,
  });
};

const getNewsletterHubEngagementMessage = async (engagementMessageId: number) => {
  const { data } = await axios.get<{ data: DTOEngagementMessage }>(
    `/json/newsletter-hub/get-engagement-message?engagement_message_id=${engagementMessageId}`
  );
  return data.data;
};

const getNewsletterHubConfig = async (engagementId: number) => {
  const { data } = await axios.post<{ data: NewsletterConfig }>("/json/newsletter-hub/getNewsletterHubConfig", {
    engagement_id: engagementId,
  });
  return data.data;
};

const getNewsletterHubFetchAudience = async (engagementId: number, signal?: AbortSignal) => {
  const { data } = await axios.post(
    "/json/newsletter-hub/fetchAudience",
    {
      engagement_id: engagementId,
    },
    {
      signal: signal,
    }
  );
  return data.data;
};

const countCustomersWithoutPhone = async () => {
  const { data } = await axios.get("/tracker/count-customers-without-phone");
  return data.data;
};

const updateNewsletterHubConfig = async (engagementId: number, config: string) => {
  await axios.post("/json/newsletter-hub/updateNewsletterHubConfig", {
    engagement_id: engagementId,
    config: config,
  });
};

const getAvailableFlows = async () => {
  const { data } = await axios.get<DTOEngagementsSmsSuggestion>("/engagements/sms/available-flows");
  return data;
};

const postAddSMSToFlows = async (flows: number[]) => {
  await Promise.all(flows.map((flow) => axios.post(`/engagements/${flow}/sms/enable`)));
};

const getEngagementMessageSettings = async (engagementMessageId: number) => {
  const { data } = await axios.get<DTOEngagementMessageSettings>(
    `/engagements/engagement-message-settings/` + engagementMessageId
  );
  return data;
};

const getSender = async () => {
  const { data } = await axios.get<Sender>("/emaillabs/sms/sender");
  return data;
};

const postSender = async (sender: string) => {
  await axios.put(`/emaillabs/sms/sender`, {
    sender,
  });
};

const sendTestSMS = async (numbers: string, engagement_message_id: number) => {
  const { data } = await axios.post(`/engagements/sendSmsTestForSingleMessage`, {
    engagement_message_id,
    numbers,
  });
  return data;
};

const sendTestSMSWithContent = async (numbers: string, content: string) => {
  const { executeRecaptcha } = useCaptcha();
  const token = await executeRecaptcha("SEND_TEST");
  const { data } = await axios.post(`/sms/send-test`, {
    message: content,
    phone_numbers: numbers,
    captcha: token,
  });
  return data;
};

const getSmsSuggestion = async (engagementId: number) => {
  const { data } = await axios.get<DTOEngagementSmsSuggestion>("/engagements/sms/suggestion", {
    params: {
      engagement_id: engagementId,
    },
  });
  return data;
};

const activateSmsSuggestion = async (engagementId: number, engagementMessageId: number, text: string) => {
  await axios.post("/engagements/sms/suggestion/active", {
    engagement_id: engagementId,
    engagement_message_id: engagementMessageId,
    text: text,
  });
};

export {
  getSMSPackages,
  getSMSCredits,
  getSMSStatistics,
  postSMSSubscription,
  getSMSSubscription,
  deleteSMSSubscription,
  getNewsletterHubSMSCampaignPreview,
  postNewsletterHubSMSCampaignCreate,
  getAvailableFlows,
  postAddSMSToFlows,
  getEngagementMessageSettings,
  sendTestSMS,
  sendTestSMSWithContent,
  getSender,
  postSender,
  getNewsletterHubEngagementMessage,
  getNewsletterHubConfig,
  updateNewsletterHubConfig,
  getNewsletterHubFetchAudience,
  postNewsletterHubRun,
  postNewsletterHubCreateNewSMSCampaign,
  toggleShortenLinks,
  getSmsSuggestion,
  countCustomersWithoutPhone,
  activateSmsSuggestion,
};
